/**
 * Proactive Token Refresh Hook
 *
 * This hook monitors the Cognito access token expiry and proactively
 * refreshes it before expiration to prevent session interruptions.
 *
 * Best Practices Implemented:
 * 1. Proactive refresh 5 minutes before expiry
 * 2. Background refresh without blocking UI
 * 3. Fallback to logout if refresh fails
 * 4. Activity-based refresh (only when user is active)
 */

import { useEffect, useRef, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { handleAuthError } from '@/api/lib/error-handler'

// Refresh tokens 5 minutes before expiry
const REFRESH_BUFFER_SECONDS = 5 * 60

// Check token status every minute
const CHECK_INTERVAL_MS = 60 * 1000

// Don't refresh if user has been inactive for 30 minutes
const INACTIVITY_THRESHOLD_MS = 30 * 60 * 1000

interface UseTokenRefreshOptions {
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Callback when session is refreshed */
  onRefresh?: () => void
  /** Callback when session expires and user must re-login */
  onSessionExpired?: () => void
}

export function useTokenRefresh({
  isAuthenticated,
  onRefresh,
  onSessionExpired,
}: UseTokenRefreshOptions) {
  const lastActivityRef = useRef<number>(Date.now())
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isRefreshingRef = useRef(false)

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
  }, [])

  // Check if user is still active
  const isUserActive = useCallback(() => {
    const timeSinceActivity = Date.now() - lastActivityRef.current
    return timeSinceActivity < INACTIVITY_THRESHOLD_MS
  }, [])

  // Check and refresh token if needed
  const checkAndRefreshToken = useCallback(async () => {
    // Skip if not authenticated or already refreshing
    if (!isAuthenticated || isRefreshingRef.current) {
      return
    }

    // Skip if user has been inactive (no need to keep session alive)
    if (!isUserActive()) {
      console.log('🔐 [TokenRefresh] User inactive, skipping proactive refresh')
      return
    }

    try {
      const session = await fetchAuthSession()

      if (!session.tokens?.accessToken) {
        console.log('🔐 [TokenRefresh] No tokens found, session invalid')
        onSessionExpired?.()
        return
      }

      const accessToken = session.tokens.accessToken
      const exp = accessToken.payload.exp as number

      if (!exp) {
        console.warn('🔐 [TokenRefresh] Token has no exp claim, cannot determine expiry')
        return
      }

      const now = Math.floor(Date.now() / 1000)
      const timeUntilExpiry = exp - now

      // Check if token is about to expire
      if (timeUntilExpiry <= REFRESH_BUFFER_SECONDS) {
        console.log(`🔐 [TokenRefresh] Token expires in ${timeUntilExpiry}s, refreshing...`)

        isRefreshingRef.current = true

        try {
          const refreshedSession = await fetchAuthSession({ forceRefresh: true })

          if (refreshedSession.tokens?.accessToken) {
            console.log('🔐 [TokenRefresh] Token refreshed successfully')
            onRefresh?.()
          } else {
            throw new Error('Refresh returned no tokens')
          }
        } catch (refreshError) {
          console.error('🔐 [TokenRefresh] Proactive refresh failed:', refreshError)

          // If refresh fails, try once more with a slight delay
          await new Promise(resolve => setTimeout(resolve, 1000))

          try {
            const retrySession = await fetchAuthSession({ forceRefresh: true })
            if (retrySession.tokens?.accessToken) {
              console.log('🔐 [TokenRefresh] Token refresh succeeded on retry')
              onRefresh?.()
            } else {
              throw new Error('Retry refresh returned no tokens')
            }
          } catch (retryError) {
            console.error('🔐 [TokenRefresh] Refresh retry failed, session expired:', retryError)
            // Session is truly expired, handle auth error
            await handleAuthError(
              { status: 401, message: 'Session expired' },
              undefined,
              true // skipRefresh since we just tried
            )
            onSessionExpired?.()
          }
        } finally {
          isRefreshingRef.current = false
        }
      } else {
        console.log(`🔐 [TokenRefresh] Token valid for ${Math.floor(timeUntilExpiry / 60)} more minutes`)
      }
    } catch (error) {
      console.error('🔐 [TokenRefresh] Error checking token:', error)
    }
  }, [isAuthenticated, isUserActive, onRefresh, onSessionExpired])

  // Set up activity listeners and check interval
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up if not authenticated
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
      return
    }

    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true })
    })

    // Check token immediately on mount
    checkAndRefreshToken()

    // Set up periodic check
    checkIntervalRef.current = setInterval(checkAndRefreshToken, CHECK_INTERVAL_MS)

    return () => {
      // Clean up event listeners
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity)
      })

      // Clean up interval
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
    }
  }, [isAuthenticated, checkAndRefreshToken, updateActivity])

  // Return methods for manual control if needed
  return {
    /** Manually trigger a token check/refresh */
    checkToken: checkAndRefreshToken,
    /** Update activity timestamp (e.g., after API call) */
    updateActivity,
  }
}
